import { AnswerTemplate } from "../../db/models/Answer";
import { FormInterstitial, QuestionGroup, FormQuestionGroup } from "../../db/models/Form";

type Page = QuestionGroup | FormQuestionGroup | FormInterstitial;
type Pages = Array<FormQuestionGroup | FormInterstitial> | Array<QuestionGroup | FormInterstitial>;

export function onlyIfMatches(
  onlyIf: Record<string, string> | Record<string, string[]>,
  answers: Record<string, AnswerTemplate[]>,
) {
  return Object.entries(onlyIf).every(([questionId, matches]: [string, string | string[]]) =>
    Array.isArray(matches)
      ? matches.some((match) => anyAnswerMatches(answers[questionId], match))
      : anyAnswerMatches(answers[questionId], matches),
  );
}

// Checks if the list of answers for a particular question contains a particular option
function anyAnswerMatches(answers: AnswerTemplate[] | undefined, optionId: string): boolean {
  return answers ? answers.some((answer) => answer.optionId === optionId) : false;
}

export function shouldSkipPage(page: Page, answers: Record<string, AnswerTemplate[]>) {
  if (!page.onlyIf) {
    return false;
  }
  return !onlyIfMatches(page.onlyIf, answers);
}

export function nextPage(currentPage: number, pages: Pages, answers: Record<string, AnswerTemplate[]>) {
  do {
    currentPage += 1;
  } while (currentPage <= pages.length && shouldSkipPage(pages[currentPage - 1], answers));
  return currentPage;
}

export function endPage(pages: Pages) {
  if (pages[pages.length - 1].type === "INTERSTITIAL") {
    return pages.length;
  }
  return pages.length + 1;
}

export function shownQuestions(pages: Pages, answers: Record<string, AnswerTemplate[]>) {
  // Run through the form starting at page 1, and note all the question IDs we encounter, in the order that we encounter them.
  const questionsToUse: string[] = [];
  for (let page = 1; page <= pages.length; page = nextPage(page, pages, answers)) {
    const group = pages[page - 1];
    if (group.type === "INTERSTITIAL") continue;
    for (const question of group.questions) {
      const questionId = typeof question === "string" ? question : question.id;
      if (!questionsToUse.includes(questionId)) {
        questionsToUse.push(questionId);
      }
    }
  }
  return questionsToUse;
}
